import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ApiSession } from '../api/client';
import type {
  AuthContextDevSubject,
  AuthContextResponse,
  MeResponse,
  OrganizationAccountView,
} from '../api/types';
import { useApi } from '../api/api-provider';
import { BrowserOidcClient, BrowserOidcTemporaryError } from './browser-oidc';
import { extractAuthCallbackParams } from './auth-routing';
import { publicOrganizationReturn } from './public-organization-routing';

/**
 * Session provider — sign-in / sign-out baseline.
 *
 *   - On mount the provider fetches `/auth/context` so the app knows
 *     whether browser sign-in should come from an external OIDC redirect
 *     handoff or from the local dev-bypass flow. The dev subject list is
 *     **only** shipped by the API when dev bypass is enabled — there is no
 *     production code path that exposes it.
 *
 *   - Authenticated identity is derived from `GET /me`. Roles, permissions,
 *     tenant code/displayName/defaultLocale all come from the API; the web
 *     never invents them locally. That keeps tenant isolation honest: the
 *     active tenant is whatever the bearer/dev-bypass principal resolves
 *     to server-side, not whatever the user types in a free-form field.
 *
 *   - Only local/pilot mock bearer tokens may use sessionStorage. External
 *     OIDC tokens stay in memory; sessionStorage holds the one-use PKCE
 *     transaction. A reload needs a new Code flow (IdP SSO may reuse login).
 */

export interface SessionUser {
  readonly subject: string;
  readonly displayName: string;
  readonly email?: string;
  readonly roles: ReadonlyArray<string>;
  readonly permissions: ReadonlyArray<string>;
  readonly preferredLocale?: string;
}

export interface SessionTenant {
  readonly id: string;
  readonly code: string;
  readonly displayName: string;
  readonly defaultLocale: string;
  /** Server-resolved public address hint; never authority or a membership claim. */
  readonly publicSlug?: string;
}

export type SessionStatus =
  | 'loading'
  | 'unauthenticated'
  | 'authenticated'
  | 'error';

export interface SessionValue {
  readonly account?: OrganizationAccountView;
  readonly managedMembership: boolean;
  readonly selectOrganization: (code: string, onSelected?: (membership: MeResponse) => void) => Promise<void>;
  readonly status: SessionStatus;
  readonly isAuthenticated: boolean;
  readonly appEnv: AuthContextResponse['appEnv'] | undefined;
  readonly authPosture: AuthContextResponse['posture'] | undefined;
  readonly authMode: AuthContextResponse['mode'] | undefined;
  readonly browserLoginUrl: string | undefined;
  readonly browserOidcConfigured: boolean;
  readonly devBypassEnabled: boolean;
  readonly authReadiness: AuthContextResponse['readiness'] | undefined;
  readonly availableDevSubjects: ReadonlyArray<AuthContextDevSubject>;

  readonly user: SessionUser | undefined;
  readonly tenant: SessionTenant | undefined;
  readonly tenantCode: string;

  readonly signInWithDevSubject: (subject: string) => Promise<void>;
  readonly beginExternalSignIn: (returnTo?: string | null, locale?: string) => Promise<void>;
  readonly completeExternalSignIn: (location: { readonly search: string; readonly hash: string }) => Promise<string | undefined>;
  readonly signOut: () => void;
  readonly error: string | undefined;

  readonly toApiSession: (locale?: string) => ApiSession;

  // Role-aware predicates (derived from `/me`, never from local guesses).
  readonly hasRole: (role: string) => boolean;
  readonly hasAnyRole: (...roles: ReadonlyArray<string>) => boolean;
  readonly hasPermission: (permission: string) => boolean;

  readonly isAttendee: boolean;
  readonly isOrganizer: boolean;
  readonly isReviewer: boolean;
  readonly isAdmin: boolean;
  readonly isPlatformAdmin: boolean;

  readonly canManageIdentity: boolean;
  readonly canViewReporting: boolean;
  readonly canExportReporting: boolean;
  readonly canViewEdocation: boolean;
  readonly canManageEdocation: boolean;
  readonly canManagePlatform: boolean;
  readonly canAccessAttendeeWorkspace: boolean;
  readonly canAccessAdminWorkspace: boolean;
}

const DEV_SUBJECT_STORAGE_KEY = 'event.session.dev-user';
const ACCESS_TOKEN_STORAGE_KEY = 'event.session.access-token';
const FALLBACK_TENANT_CODE = 'eDO';

const SessionContext = createContext<SessionValue | undefined>(undefined);

function readPersistedDevSubject(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try { return window.localStorage.getItem(DEV_SUBJECT_STORAGE_KEY) ?? undefined; } catch { return undefined; }
}

function persistDevSubject(value: string | undefined): void {
  if (typeof window === 'undefined') return;
  if (value) {
    window.localStorage.setItem(DEV_SUBJECT_STORAGE_KEY, value);
  } else {
    try { window.localStorage.removeItem(DEV_SUBJECT_STORAGE_KEY); } catch { /* No active dev identity remains in memory. */ }
  }
}

function readPersistedAccessToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try { return window.sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) ?? undefined; } catch { return undefined; }
}

function persistAccessToken(value: string | undefined): void {
  if (typeof window === 'undefined') return;
  if (value) {
    window.sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, value);
  } else {
    try { window.sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY); } catch { /* External mode never restores this value. */ }
  }
}

export function SessionProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const client = useApi();
  const [authContext, setAuthContext] = useState<AuthContextResponse | undefined>(
    undefined,
  );
  const [me, setMe] = useState<MeResponse | undefined>(undefined);
  const [account, setAccount] = useState<OrganizationAccountView | undefined>(undefined);
  const [devSubject, setDevSubject] = useState<string | undefined>(() =>
    readPersistedDevSubject(),
  );
  const [accessToken, setAccessToken] = useState<string | undefined>(() =>
    readPersistedAccessToken(),
  );
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [error, setError] = useState<string | undefined>(undefined);
  const sessionEpoch = useRef(0);
  const publicEntryPending = useRef(false);
  const mounted = useRef(true);
  const oidc = useMemo(() => authContext?.browserOidc ? new BrowserOidcClient(authContext.browserOidc, {
    origin: window.location.origin, storage: window.sessionStorage, crypto: window.crypto,
  }) : undefined, [authContext?.browserOidc?.issuer, authContext?.browserOidc?.clientId]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; sessionEpoch.current += 1; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    client
      .getAuthContext()
      .then((ctx) => {
        if (cancelled) return;
        if (ctx.mode === 'external_oidc') {
          // Never restore a legacy token URL handoff in external mode.
          persistAccessToken(undefined);
          setAccessToken(undefined);
        }
        setAuthContext(ctx);
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAuthContext(undefined);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'auth_context_failed');
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    if (!authContext) return;

    const sessionCandidate: ApiSession | undefined = accessToken
      ? {
          tenantCode: FALLBACK_TENANT_CODE,
          bearerToken: accessToken,
        }
      : authContext.devBypassEnabled && devSubject
        ? {
            tenantCode: FALLBACK_TENANT_CODE,
            devUser: devSubject,
          }
        : undefined;

    if (!sessionCandidate) {
      setMe(undefined);
      setAccount(undefined);
      setStatus('unauthenticated');
      setError((previous) => previous === 'oidc.session_expired' ? previous : undefined);
      return;
    }

    let cancelled = false;
    const epoch = sessionEpoch.current;
    const isCurrent = () => !cancelled && mounted.current && epoch === sessionEpoch.current;
    setStatus('loading');
    setError(undefined);
    (async () => {
      if (authContext.membershipMode === 'managed') {
        const profile = await client.getAccount(sessionCandidate);
        if (!isCurrent()) return undefined;
        setAccount(profile);
        // Public slugs are independent from internal tenant codes. Let the
        // public entry resolve the code and verify that exact membership;
        // never auto-select an unrelated organization after this login.
        if (publicEntryPending.current) return undefined;
        // With more than one active membership the user must choose explicitly.
        // The account API already excludes disabled memberships and tenants.
        if (profile.organizations.length !== 1) return undefined;
        const organization = profile.organizations[0];
        const response = await client.getMe({ ...sessionCandidate, tenantCode: organization.code });
        if (response.tenant.id !== organization.id || response.tenant.code !== organization.code) {
          throw new Error('organization.membership_required');
        }
        return response;
      }
      return client.getMe(sessionCandidate);
    })()
      .then((response) => {
        if (!isCurrent()) return;
        setMe(response);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        if (!isCurrent()) return;
        setMe(undefined);
        setAccount(undefined);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'me_failed');
      })
      .finally(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [accessToken, authContext, client, devSubject]);

  const signInWithDevSubject = useCallback(async (subject: string) => {
    sessionEpoch.current += 1;
    oidc?.clear();
    persistDevSubject(subject);
    persistAccessToken(undefined);
    setDevSubject(subject);
    setAccessToken(undefined);
    setError(undefined);
  }, [oidc]);

  const beginExternalSignIn = useCallback(async (returnTo?: string | null, locale?: string) => {
    if (!oidc || authContext?.mode !== 'external_oidc') throw new Error('oidc.not_configured');
    const url = await oidc.beginSignIn(returnTo, locale);
    if (mounted.current) window.location.assign(url);
  }, [authContext?.mode, oidc]);

  const completeExternalSignIn = useCallback(async (location: { readonly search: string; readonly hash: string }) => {
    const epoch = sessionEpoch.current;
    let bearerToken: string;
    let returnTo: string | undefined;
    if (authContext?.mode === 'external_oidc') {
      if (!oidc) throw new Error('oidc.not_configured');
      returnTo = await oidc.completeSignIn(location.search, location.hash);
      bearerToken = await oidc.getAccessToken();
      persistAccessToken(undefined);
    } else if (authContext?.mode === 'mock_oidc' && authContext.appEnv !== 'production') {
      const legacy = extractAuthCallbackParams(location);
      if (!legacy.accessToken?.trim()) throw new Error('oidc.missing_token');
      bearerToken = legacy.accessToken.trim();
      returnTo = legacy.returnTo;
      persistAccessToken(bearerToken);
    } else {
      throw new Error('oidc.not_configured');
    }
    if (!mounted.current || epoch !== sessionEpoch.current) throw new Error('oidc.session_changed');
    publicEntryPending.current = Boolean(publicOrganizationReturn(returnTo));
    persistDevSubject(undefined);
    setAccessToken(bearerToken);
    setDevSubject(undefined);
    setError(undefined);
    return returnTo;
  }, [authContext?.mode, authContext?.appEnv, oidc]);

  const signOut = useCallback(() => {
    sessionEpoch.current += 1;
    publicEntryPending.current = false;
    const logoutUrl = oidc?.logoutUrl();
    persistDevSubject(undefined);
    persistAccessToken(undefined);
    setDevSubject(undefined);
    setAccessToken(undefined);
    setMe(undefined);
    setAccount(undefined);
    setError(undefined);
    setStatus('unauthenticated');
    if (logoutUrl) window.location.assign(logoutUrl);
  }, [oidc]);

  const resolveBearerToken = useCallback(async (epoch: number) => {
    if (!oidc || epoch !== sessionEpoch.current || !mounted.current) throw new Error('oidc.session_changed');
    try {
      const token = await oidc.getAccessToken();
      if (epoch !== sessionEpoch.current || !mounted.current) throw new Error('oidc.session_changed');
      return token;
    } catch (err) {
      // Offline/identity-provider failures block this protected request while
      // preserving the current identity and memory-only refresh credential.
      // No caller receives a stale token; it must retry after connectivity returns.
      if (err instanceof BrowserOidcTemporaryError) throw err;
      if (mounted.current && epoch === sessionEpoch.current) {
        sessionEpoch.current += 1;
        setAccessToken(undefined);
        setMe(undefined);
        setStatus('unauthenticated');
        setError('oidc.session_expired');
      }
      throw err;
    }
  }, [oidc]);

  const value = useMemo<SessionValue>(() => {
    const tenantCode = me?.tenant.code ?? (authContext?.membershipMode === 'managed' ? '' : FALLBACK_TENANT_CODE);
    const user: SessionUser | undefined = me
      ? {
          subject: devSubject ?? me.principal.id,
          displayName: me.principal.displayName ?? me.principal.email ?? me.principal.id,
          email: me.principal.email,
          roles: me.principal.roles,
          permissions: me.principal.permissions,
          preferredLocale: me.principal.locale,
        }
      : account ? { subject: account.account.subject,
          displayName: account.account.displayName ?? account.account.email ?? account.account.subject,
          email: account.account.email ?? undefined, roles: [], permissions: [] } : undefined;
    const tenant: SessionTenant | undefined = me ? me.tenant : undefined;

    const roles = new Set(user?.roles ?? []);
    const permissions = new Set(user?.permissions ?? []);

    const hasRole = (r: string) => roles.has(r);
    const hasAnyRole = (...rs: ReadonlyArray<string>) => rs.some((r) => roles.has(r));
    const hasPermission = (p: string) => permissions.has(p);

    const isAttendee = hasPermission('enrollment:self');
    const isOrganizer = hasPermission('attendance:scan');
    const isReviewer = hasPermission('tasks:review');
    const isAdmin = hasPermission('events:write') || hasPermission('events:publish');
    const isPlatformAdmin = hasRole('platform_admin');
    const canManageIdentity =
      hasPermission('identity:manage') || hasRole('sys_admin');
    const canViewReporting = hasPermission('reporting:read');
    const canExportReporting = hasPermission('reporting:export');
    const canViewEdocation = hasPermission('integration:read');
    const canManageEdocation = hasPermission('integration:manage');
    const canManagePlatform = hasPermission('platform:manage');
    const canAccessAttendeeWorkspace = isAttendee;
    const canAccessAdminWorkspace =
      isAdmin || (hasPermission('events:read') && !isAttendee) ||
      isOrganizer ||
      isReviewer ||
      canManageIdentity ||
      canViewReporting ||
      canViewEdocation ||
      canManageEdocation ||
      hasPermission('privacy:read') ||
      hasPermission('branding:manage') ||
      hasPermission('notifications:manage') ||
      (authContext?.membershipMode === 'managed' && hasPermission('identity:read'));

    return {
      account,
      managedMembership: authContext?.membershipMode === 'managed',
      selectOrganization: async (code, onSelected) => {
        const localMock = authContext?.mode === 'mock_oidc' && authContext.appEnv !== 'production';
        const localDevSubject = localMock && authContext.devBypassEnabled ? devSubject : undefined;
        if ((!oidc || !accessToken) && !(localMock && (accessToken || localDevSubject))) {
          throw new Error('oidc.not_configured');
        }
        // Invalidate API snapshots belonging to the previous organization.
        const epoch = ++sessionEpoch.current;
        const candidate: ApiSession = oidc && accessToken
          ? { tenantCode: '', bearerToken: await resolveBearerToken(epoch) }
          : accessToken
            ? { tenantCode: '', bearerToken: accessToken }
            : { tenantCode: '', devUser: localDevSubject };
        const profile = await client.getAccount(candidate);
        if (!mounted.current || epoch !== sessionEpoch.current) throw new Error('oidc.session_changed');
        const organization = profile.organizations.find((org) => org.code === code);
        if (!organization) throw new Error('organization.membership_required');
        const response = await client.getMe({ ...candidate, tenantCode: code });
        if (!mounted.current || epoch !== sessionEpoch.current) throw new Error('oidc.session_changed');
        if (response.tenant.id !== organization.id || response.tenant.code !== organization.code) {
          throw new Error('organization.membership_required');
        }
        setAccount(profile);
        setMe(response);
        publicEntryPending.current = false;
        // The tenant-scoped layout remounts on identity changes. Complete its
        // navigation in this same verified handoff, not in disposable page state.
        onSelected?.(response);
      },
      status,
      isAuthenticated: status === 'authenticated',
      appEnv: authContext?.appEnv,
      authPosture: authContext?.posture,
      authMode: authContext?.mode,
      browserLoginUrl: authContext?.browserLoginUrl ?? undefined,
      browserOidcConfigured: Boolean(oidc),
      devBypassEnabled: authContext?.devBypassEnabled ?? false,
      authReadiness: authContext?.readiness,
      availableDevSubjects: authContext?.devSubjects ?? [],
      user,
      tenant,
      tenantCode,
      signInWithDevSubject,
      beginExternalSignIn,
      completeExternalSignIn,
      signOut,
      error,
      toApiSession: (locale) => {
        const epoch = sessionEpoch.current;
        return {
          tenantCode, devUser: devSubject, bearerToken: accessToken, locale,
          getBearerToken: oidc && accessToken ? () => resolveBearerToken(epoch) : undefined,
        };
      },
      hasRole,
      hasAnyRole,
      hasPermission,
      isAttendee,
      isOrganizer,
      isReviewer,
      isAdmin,
      isPlatformAdmin,
      canManageIdentity,
      canViewReporting,
      canExportReporting,
      canViewEdocation,
      canManageEdocation,
      canManagePlatform,
      canAccessAttendeeWorkspace,
      canAccessAdminWorkspace,
    };
  }, [
    accessToken,
    account,
    client,
    authContext,
    beginExternalSignIn,
    completeExternalSignIn,
    devSubject,
    error,
    me,
    oidc,
    resolveBearerToken,
    signInWithDevSubject,
    signOut,
    status,
  ]);

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession() must be used inside <SessionProvider>.');
  }
  return ctx;
}

/**
 * Pick the deterministic post-sign-in landing route based on the user's
 * effective roles. The order encodes the workspace separation the product
 * spec calls for: platform > tenant admin > organizer > reviewer > attendee.
 * Manager/HR/Auditor land in the admin reporting workspace because that is
 * the only baseline surface they have meaningful read access to.
 *
 * The platform plane wins when present, but that does NOT revoke any
 * tenant-plane workspace the user may also have — `describeEffectiveWorkspaces`
 * returns every surface the user is entitled to so the UI can offer an
 * explicit switch. There is intentionally no super-shell: the two
 * surfaces stay separate URLs.
 */
export function pickDefaultLandingForRoles(
  roles: ReadonlyArray<string>,
): string {
  const set = new Set(roles);
  if (set.has('platform_admin')) return '/platform';
  if (hasAdminWorkspaceRoleSet(set)) return pickAdminLandingForRoles(roles);
  if (set.has('attendee')) return '/attendee';
  return '/';
}

export type WorkspaceSurface = 'platform' | 'admin' | 'attendee';

export interface WorkspaceDescriptor {
  readonly surface: WorkspaceSurface;
  readonly path: string;
  readonly labelKey: string;
}

export interface WorkspacesDescription {
  readonly defaultLanding: string;
  readonly available: ReadonlyArray<WorkspaceDescriptor>;
}

/**
 * Enumerate every workspace surface the user is entitled to given
 * their resolved roles. Platform and tenant surfaces are listed
 * independently so a user who is both `platform_admin` and a tenant
 * role (e.g. `sys_admin`) sees both entries and can pick explicitly,
 * rather than having one silently supersede the other. The default
 * landing is still deterministic (see `pickDefaultLandingForRoles`).
 */
export function describeEffectiveWorkspaces(
  roles: ReadonlyArray<string>,
): WorkspacesDescription {
  const set = new Set(roles);
  const available: WorkspaceDescriptor[] = [];
  if (set.has('platform_admin')) {
    available.push({
      surface: 'platform',
      path: '/platform',
      labelKey: 'session.workspace.platform',
    });
  }
  if (hasAdminWorkspaceRoleSet(set)) {
    available.push({
      surface: 'admin',
      path: pickAdminLandingForRoles(roles),
      labelKey: 'session.workspace.admin',
    });
  }
  if (set.has('attendee')) {
    available.push({
      surface: 'attendee',
      path: '/attendee',
      labelKey: 'session.workspace.attendee',
    });
  }
  return {
    defaultLanding: pickDefaultLandingForRoles(roles),
    available,
  };
}

export function pickAdminLandingForRoles(
  roles: ReadonlyArray<string>,
): string {
  const set = new Set(roles);
  if (set.has('sys_admin')) return '/admin';
  if (set.has('privacy_officer')) return '/admin/organization/privacy';
  if (set.has('event_admin')) return '/admin/events';
  if (set.has('organizer')) return '/admin/scanner';
  if (set.has('reviewer')) return '/admin/reviews';
  if (set.has('manager') || set.has('hr') || set.has('auditor')) {
    return '/admin/reporting';
  }
  return '/admin';
}

function hasAdminWorkspaceRoleSet(roles: ReadonlySet<string>): boolean {
  return (
    roles.has('sys_admin') ||
    roles.has('event_admin') ||
    roles.has('organizer') ||
    roles.has('reviewer') ||
    roles.has('manager') ||
    roles.has('hr') ||
    roles.has('auditor') ||
    roles.has('privacy_officer')
  );
}
